import rtf from 'jsrtf'

import actions from './actions/index'

import {
  onlyValidKnownFiles,
  validKnownFileOrNull,
  validCreatedOfflineFilesEntryOrNull,
  validTrialOrNull,
  keepOnlyValidCustomTemplates,
  fixInvalidCustomTemplate,
  keepOnlyValidTemplates,
  validExportSettingsOrDefault,
  validAppSettings,
  keepOnlyValidBackups,
  keepOnlyValidImageCacheEntries,
  validLicenseOrNull,
  keepOnlyValidOfflineFiles,
  keepOnlyValidCreatedOfflineFiles,
} from './types/system'
import {
  validBuiltInTemplateOrNull,
  validPlottrFileOrNull,
} from './types/plottrFileSchemaValidation.js'

import * as ActionTypes from './constants/ActionTypes'
import * as LoadActions from './constants/loadActions'
import * as colors from './constants/CSScolors'
import * as errorCodes from './constants/errorCodes'

import * as lineHelpers from './helpers/lines'
import * as cardHelpers from './helpers/cards'
import * as beatHelpers from './helpers/beats'
import * as bookHelpers from './helpers/books'
import * as listHelpers from './helpers/lists'
import * as orientedClassNameHelpers from './helpers/orientedClassName'
import * as hierarchyHelpers from './helpers/hierarchy'
import * as colorHelpers from './helpers/colors'
import * as timeHelpers from './helpers/time'
import * as dateHelpers from './helpers/date'
import * as fileHelpers from './helpers/file'
import * as templatesHelpers from './helpers/templates'
import * as characterHelpers from './helpers/characters'
import * as uiHelpers from './helpers/ui'
import * as textHelpers from './helpers/text'
import * as jsonHelpers from './helpers/json'
import * as promiseHelpers from './helpers/promise'
import * as safeParseInt from './helpers/safeParseInt'
import * as hash from './helpers/hash'
import * as stringHelpers from './helpers/string'

import * as versionControl from './version_control/index'

import * as pocketTypes from './types/plottr-in-your-pocket'

import * as template from './template/index'

import * as editStates from './constants/editStates'

import migrateIfNeeded from './migrator/migration_manager'
import Migrator from './migrator/migrator.js'
import applyAllFixes, {
  addHierarchiesIfMissing,
  addUITimelineOrHierarchiesStateIfMissing,
} from './migrator/handleSpecialCases'

import selectors from './selectors/index'

import rootReducer from './reducers/root'
import mainReducer from './reducers/main'
import {
  SYSTEM_REDUCER_KEYS,
  SYSTEM_REDUCER_ACTION_TYPES,
  removeSystemKeys,
} from './reducers/systemReducers'
import customAttributesReducer from './reducers/customAttributes'
import linesReducer from './reducers/lines'
import beatsReducer from './reducers/beats'
import booksReducer from './reducers/books'
import cardsReducer from './reducers/cards'
import categoriesReducer from './reducers/categories'
import charactersReducer from './reducers/characters'
import imagesReducer from './reducers/images'
import notesReducer from './reducers/notes'
import placesReducer from './reducers/places'
import seriesReducer from './reducers/series'
import tagsReducer from './reducers/tags'
import fileReducer from './reducers/file'
import uiReducer from './reducers/ui'
import hierarchyReducer from './reducers/hierarchy'
import errorReducer from './reducers/error'
import permissionReducer from './reducers/permission'
import clientReducer from './reducers/client'
import licenseReducer from './reducers/license'
import knownFilesReducer from './reducers/knownFiles'
import templatesReducer from './reducers/templates'
import settingsReducer from './reducers/settings'
import backupsReducer from './reducers/backups'
import applicationStateReducer from './reducers/applicationState'
import imageCacheReducer from './reducers/imageCache'
import notificationsReducer from './reducers/notifications'
import domEventsReducer from './reducers/domEvents'
import testingAndDiagnosisReducer from './reducers/testingAndDiagnosis'
import familyTreesReducer from './reducers/familyTree'
import ideasReducer from './reducers/ideas'
import audioCacheReducer from './reducers/audioCache'

import * as initialState from './store/initialState'
import * as lineColors from './store/lineColors'
import { emptyFile } from './store/newFileState'
import * as newIds from './store/newIds'
import * as borderStyle from './store/borderStyle'
import * as defaultSettings from './store/defaultSettings'
import { roughlyMatchesPlottrFileSchema } from './types/plottr'
import * as plottrSchema from './types/plottrFileSchemaValidation'

import externalSync, { externalSyncWithoutHistory } from './middlewares/externalSync'

import { ARRAY_KEYS } from './middlewares/array-keys'

import * as tree from './reducers/tree'

// Slate serialisers
import serializeToRTF from './slate_serializers/to_rtf'
import {
  serialize as serializeToPlain,
  serializeNoFormatting,
} from './slate_serializers/to_plain_text'
import {
  convertHTMLString,
  convertHTMLNodeList,
  validHTMLRCESchema,
  validRCESchema,
} from './slate_deserializers/from_html'
import { rtfToHTML, slateToHTML } from './slate_serializers/to_html'

import checkFileIntegrity from './store/checkFileIntegrity'
import accountReducer from './reducers/account'

/**
 * @typedef Logger
 * @property {function(String): Void} info
 * @property {function(String): Void} warn
 * @property {function(Error, String): Void} error
 */

/**
 * @typedef IsInsertPlaceholder
 * @property {boolean} [isInsertChildCell]
 */

/**
 * @typedef {Beat & IsInsertPlaceholder} MaybeTopLevelBeat
 */

/**
 * @typedef TimelineBundle
 * @property {TimelineScrollPosition} timelineScrollPosition
 * @property {number | 'series'} currentTimeline
 * @property {'horizontal' | 'vertical'} orientation
 * @property {'small' | 'medium' | 'large'} timelineSize
 * @property {boolean} timelineIsExpanded
 * @property {boolean} darkMode
 * @property {boolean} attributesDialogIsOpen
 * @property {boolean} filterIsEmpty
 * @property {boolean} isSmall
 * @property {boolean} isMedium
 * @property {boolean} isLarge
 */

/**
 * @typedef CardMetadata
 * @property {Card['id']} id
 * @property {Card['beatId']} beatId
 * @property {Card['lineId']} lineId
 * @property {Card['tags']} tags
 * @property {Card['color']} color
 * @property {Card['places']} places
 * @property {Card['characters']} characters
 * @property {Card['bookId']} bookId
 * @property {Card['title']} title
 * @property {Card['templates']} templates
 * @property {Card['positionWithinLine']} positionWithinLine
 * @property {boolean} hasDescription
 * @property {Card['notes']} notes
 * @property {Card['audioClipId']} audioClipId
 */

/**
 * @typedef Licenses
 * @property {PlottrLicense | Null} plottrLicense
 * @property {ProLicense | Null} proLicense
 */

/**
 * @template A
 * @typedef {Array<CategorisedEntitiesCategory<A>>} CategorisedEntities
 */

/**
 * @template A
 * @typedef {Omit<Category, 'id'> & { id: number | null, items: Array<A> }} CategorisedEntitiesCategory
 */

/**
 * @typedef {import('redux').Store<PlottrState, PltrAction>} PlottrStore
 */

/**
 * @template A
 * @typedef {import('./types/plottr').Importable<A>} Importable
 */

/**
 * @typedef {function(PltrAction | function(Dispatch, GetState): Void): Void} Dispatch
 * @typedef {function(): PlottrState} GetState
 */

/**
 * @typedef {import('./types/plottr').PlottrFile} PlottrFile
 * @typedef {import('./types/plottr').PlottrFileFile} File
 * @typedef {import('./types/plottr').PlottrFileUi} PlottrFileUi
 * @typedef {import('./types/plottr').FeatureFlags} FeatureFlags
 * @typedef {import('./types/plottr').Attributes} Attributes
 * @typedef {import('./types/plottr').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('./types/plottr').LegacyCustomAttributes} LegacyCustomAttributes
 * @typedef {import('./types/plottr').AttributeValue} AttributeValue
 * @typedef {import('./types/plottr').Attribute} Attribute
 * @typedef {import('./types/plottr').Beat} Beat
 * @typedef {import('./types/plottr').HierarchyLevel} HierarchyLevel
 * @typedef {import('./types/plottr').PlottrFileImages} PlottrFileImages
 * @typedef {import('./types/plottr').PlottrFileAudioClips} PlottrFileAudioClips
 * @typedef {import('./types/plottr').Slate} Slate
 * @typedef {import('./types/plottr').SlateRootElement} SlateRootElement
 * @typedef {import('./types/plottr').BulletedList} BulletedList
 * @typedef {import('./types/plottr').NumberedList} NumberedList
 * @typedef {import('./types/plottr').ListChild} ListChild
 * @typedef {import('./types/plottr').AnySlateNode} AnySlateNode
 * @typedef {import('./types/plottr').TextChild} TextChild
 * @typedef {import('./version_control/index').SlatePatch} SlatePatch
 * @typedef {import('./types/system').SystemState} SystemState
 * @typedef {typeof import('./selectors/allSelectors').allSelectors} Selectors
 * @typedef {import('./types/plottr').ImageRecord} ImageRecord
 * @typedef {import('./types/plottr').RelationshipConfiguration} RelationshipConfiguration
 * @typedef {import('./types/plottr').FamilyTree} FamilyTree
 * @typedef {import('./types/plottr').PlottrFileFamilyTrees} PlottrFileFamilyTrees
 * @typedef {import('./types/plottr').LineStyle} LineStyle
 * @typedef {import('./types/plottr').Member} Member
 * @typedef {import('./types/plottr').Relationship} Relationship
 * @typedef {import('./types/plottr').RelationshipType} RelationshipType
 * @typedef {import('./types/plottr').Translation} Translation
 * @typedef {import('./types/plottr').DOMSelection} DOMSelection
 * @typedef {import('./types/plottr').SlateSelection} SlateSelection
 * @typedef {import('./types/plottr').SlateImageData} SlateImageData
 * @typedef {import('./types/plottr').SlateImageLink} SlateImageLink
 * @typedef {import('./types/plottr').DOMSelection} Selection
 * @typedef {import('./types/plottr').PreviewPanelItem} PreviewPanelItem
 * @typedef {import('./types/plottr').TagsPlottrFileEntry} Tag
 * @typedef {import('./types/plottr').CharactersPlottrFileEntry} Character
 * @typedef {import('./types/plottr').PlacesPlottrFileEntry} Place
 * @typedef {import('./types/plottr').NotesPlottrFileEntry} Note
 * @typedef {import('./types/plottr').PlottrFileCard} Card
 * @typedef {import('./types/plottr').PlottrFileBook} PlottrFileBook
 * @typedef {import('./types/plottr').PreviewPanelEntityKind} PreviewPanelEntityKind
 * @typedef {import('./types/plottr').EntityKind} EntityKind
 * @typedef {import('./types/plottr').SearchHit} SearchHit
 * @typedef {import('./types/plottr').PlottrFileSeries} PlottrFileSeries
 * @typedef {import('./types/plottr').Line} Line
 * @typedef {import('./types/plottr').Focus} Focus
 * @typedef {import('./types/plottr').QuickEditModal} QuickEditModal
 * @typedef {import('./types/plottr').BookmarkItem} BookmarkItem
 * @typedef {import('./types/plottr').BookmarkEntityKind} BookmarkEntityKind
 * @typedef {import('./types/plottr').IdeaItem} IdeaItem
 * @typedef {import('./types/plottr').AudioClipRecord} AudioClipRecord
 * @typedef {import('./types/plottr').Category} Category
 * @typedef {import('./types/plottr').AttributeType} AttributeType
 * @typedef {import('./types/plottr').TextAttributeData} TextAttributeData
 * @typedef {import('./types/plottr').PlottrFileBook} Book
 * @typedef {import('./types/plottr').PlottrFileBooks} Books
 * @typedef {import('./types/plottr').PlottrFileSeries} Series
 * @typedef {import('./types/plottr').IdeaConfirmModal} IdeaConfirmModal
 * @typedef {import('./types/plottr').IdeaBaseData} IdeaBaseData
 * @typedef {import('./types/plottr').PlottrFileBeatsEntry} PlottrFileBeatsEntry
 * @typedef {import('./types/plottr').PlottrFileBeats} PlottrFileBeats
 * @typedef {import('./types/plottr').CardDialogUi} CardDialogUi
 * @typedef {import('./types/plottr').ImportModalUiData} ImportModalUiData
 * @typedef {import('./types/plottr').HierarchyLevelsPlottrFile} HierarchyLevels
 * @typedef {import('./types/plottr').HierarchyLevels} PerBookHierarchyLevels
 * @typedef {import('./types/plottr').TimelineScrollPosition} TimelineScrollPosition
 * @typedef {import('./types/plottr').CategoriesPlottrFile} FileCategories
 * @typedef {import('./types/plottr').PlottrTab} PlottrTab
 * @typedef {import('./types/plottr').TimelineOrientation} TimelineOrientation
 * @typedef {import('./types/plottr').TimelineSize} TimelineSize
 * @typedef {import('./types/plottr').CardColorSource} CardColorSource
 * @typedef {import('./types/plottr').OutlineFulltextHoverDetails} OutlineFulltextHoverDetails
 * @typedef {import('./types/plottr').PlottrFileEntity} PlottrFileEntity
 * @typedef {import('./types/plottr').PlottrThemes} PlottrThemes
 * @typedef {import('./selectors/uiThirdOrder').AllSearchHits} AllSearchHits
 * @typedef {import('./types/plottr-in-your-pocket').Idea} Idea
 * @typedef {import('./types/plottr-in-your-pocket').UniversalIdeaId} UniversalIdeaId
 * @typedef {import('./types/plottr-in-your-pocket').UniversalThreadId} UniversalThreadId
 * @typedef {import('./types/plottr-in-your-pocket').Thread} Thread
 * @typedef {import('./types/plottr-in-your-pocket').FileURI} FileURI
 * @typedef {import('./types/plottr-in-your-pocket').PhotoIdea} PhotoIdea
 * @typedef {import('./types/system').ImageCache} ImageCache
 * @typedef {import('./types/system').Ideas} Ideas
 * @typedef {import('./types/system').Threads} Threads
 * @typedef {import('./types/system').AudioCache} AudioCache
 * @typedef {import('./types/system').KnownFilesEntry} KnownFilesEntry
 * @typedef {import('./types/system').TrialInfo} TrialInfo
 * @typedef {import('./types/system').BuiltInTemplate} BuiltInTemplate
 * @typedef {import('./types/system').CustomTemplate} CustomTemplate
 * @typedef {import('./types/system').SceneCustomTemplate} SceneCustomTemplate
 * @typedef {import('./types/system').CharacterCustomTemplate} CharacterCustomTemplate
 * @typedef {import('./types/system').PlotlineCustomTemplate} PlotlineCustomTemplate
 * @typedef {import('./types/system').PlotlineCustomTemplateData} PlotlineCustomTemplateData
 * @typedef {import('./types/system').SceneCustomTemplateAttribute} SceneCustomTemplateAttribute
 * @typedef {import('./types/system').CharacterCustomTemplateAttribute} CharacterCustomTemplateAttribute
 * @typedef {import('./types/system').PlotlineTemplate} PlotlineTemplate
 * @typedef {import('./types/system').PlotlineTemplateData} PlotlineTemplateData
 * @typedef {import('./types/system').ProjectTemplate} ProjectTemplate
 * @typedef {import('./types/system').ExportSettings} ExportSettings
 * @typedef {import('./types/system').UserSettings} UserSettings
 * @typedef {import('./types/system').Settings} Settings
 * @typedef {import('./types/system').MachineInfo} MachineInfo
 * @typedef {import('./types/system').AppSettings} AppSettings
 * @typedef {import('./types/system').PlottrLicense} PlottrLicense
 * @typedef {import('./types/system').ProLicense} ProLicense
 * @typedef {import('./types/system').OfflineFilesEntry} OfflineFilesEntry
 * @typedef {import('./types/system').BackupsFoldersEntry} BackupsFoldersEntry
 * @typedef {import('./types/system').BackupFolder} BackupFolder
 * @typedef {import('./types/system').Templates} Templates
 * @typedef {import('./types/system').BuiltInTemplateState} BuiltInTemplateState
 * @typedef {import('./types/system').FilePermission} FilePermission
 * @typedef {import('./types/system').ApplicationState} ApplicationState
 * @typedef {import('./types/system').ProjectApplicationState} ProjectApplicationState
 * @typedef {import('./types/system').SettingsApplicationState} SettingsApplicationState
 * @typedef {import('./types/system').FileApplicationState} FileApplicationState
 * @typedef {import('./types/system').SessionApplicationState} SessionApplicationState
 * @typedef {import('./types/system').LicenseApplicationState} LicenseApplicationState
 * @typedef {import('./types/system').SettingsWizardApplicationState} SettingsWizardApplicationState
 * @typedef {import('./types/system').ProOnboardingApplicationState} ProOnboardingApplicationState
 * @typedef {import('./types/system').UpdateApplicationState} UpdateApplicationState
 * @typedef {import('./types/system').WorkApplicationState} WorkApplicationState
 * @typedef {import('./types/system').UserInteractionsApplicationState} UserInteractionsApplicationState
 * @typedef {import('./types/system').DashboardApplicationState} DashboardApplicationState
 * @typedef {import('./types/system').BootingApplicationState} BootingApplicationState
 * @typedef {import('./types/system').ShutdownApplicationState} ShutdownApplicationState
 * @typedef {import('./types/system').MainApplicationStateToShow} MainApplicationStateToShow
 * @typedef {import('./types/system').SystemLicense} SystemLicense
 * @typedef {import('./types/system').CharacterTemplate} CharacterTemplate
 * @typedef {import('./types/system').SceneTemplate} SceneTemplate
 * @typedef {import('./types/system').GlobalFontsUserAppSettings} GlobalFontsUserAppSettings
 * @typedef {import('./types/system').RceFontsUserAppSettings} RceFontsUserAppSettings
 * @typedef {import('./types/system').HeadingsTimelineFontsUserAppSettings} HeadingsTimelineFontsUserAppSettings
 * @typedef {import('./types/system').TimelineFontsUserAppSettings} TimelineFontsUserAppSettings
 * @typedef {import('./types/system').UpdateInfo} UpdateInfo
 * @typedef {import('./types/system').CharactersWordExportSettings} CharactersWordExportSettings
 * @typedef {import('./types/system').CharactersScrivenerExportSettings} CharactersScrivenerExportSettings
 * @typedef {import('./types/system').GeneralScrivenerExportSettings} GeneralScrivenerExportSettings
 * @typedef {import('./types/system').GeneralWordExportSettings} GeneralWordExportSettings
 * @typedef {import('./types/system').NotesWordExportSettings} NotesWordExportSettings
 * @typedef {import('./types/system').NotesScrivenerExportSettings} NotesScrivenerExportSettings
 * @typedef {import('./types/system').OutlineWordExportSettings} OutlineWordExportSettings
 * @typedef {import('./types/system').OutlineScrivenerExportSettings} OutlineScrivenerExportSettings
 * @typedef {import('./types/system').PlacesWordExportSettings} PlacesWordExportSettings
 * @typedef {import('./types/system').PlacesScrivenerExportSettings} PlacesScrivenerExportSettings
 * @typedef {import('./types/system').ConflictChanges} ConflictChanges
 * @typedef {import('./types/system').LocalAndOnlineConflictChanges} LocalAndOnlineConflictChanges
 * @typedef {import('./types/system').SeriesConflict} SeriesConflict
 * @typedef {import('./types/system').DroppedBeatDomEventsSystem} DroppedBeatDomEventsSystem
 * @typedef {import('./types/system').ToastNotificationsSystem} ToastNotifications
 * @typedef {import('./types/system').LastClickDomEventsSystem} LastClickDomEvent
 * @typedef {import('./types/system').MergeBias} MergeBias
 * @typedef {import('./types/system').WordExportSettings} WordExportSettings
 * @typedef {import('./types/system').ScrivenerExportSettings} ScrivenerExportSettings
 * @typedef {import('./types/system').CreatedOfflineFiles} CreatedOfflineFiles
 * @typedef {import('./types/system').CreatedOfflineFileEntry} CreatedOfflineFileEntry
 */

/**
 * @typedef {BackupFolder & { backups: Array<BackupsFoldersEntry>, groups: Array<Array<BackupsFoldersEntry>>, longDateStr: String, shortDateStr: String }} GroupedBackpuFoldersForDisplay
 */

/**
 * @typedef CharacterMetadata
 * @property {number} id
 * @property {String | Null} categoryId
 */

/**
 * @typedef {(LegacyCustomAttribute | Attribute) & { value: Slate | String, type: AttributeType }} MixedDenormalisedAttribute
 */

/**
 * @typedef {import('./selectors/charactersThirdOrder').EntityInBookCategoryDisplay} EntityInBookCategoryDisplay
 */

/**
 * @typedef {typeof import('./actions/index').beatActions} BeatActions
 * @typedef {typeof import('./actions/index').bookActions} BookActions
 * @typedef {typeof import('./actions/index').cardActions} CardActions
 * @typedef {typeof import('./actions/index').categoryActions} CategoryActions
 * @typedef {typeof import('./actions/index').characterActions} CharacterActions
 * @typedef {typeof import('./actions/index').customAttributeActions} CustomAttributeActions
 * @typedef {typeof import('./actions/index').imageActions} ImageActions
 * @typedef {typeof import('./actions/index').audioClipActions} AudioClipActions
 * @typedef {typeof import('./actions/index').lineActions} LineActions
 * @typedef {typeof import('./actions/index').noteActions} NoteActions
 * @typedef {typeof import('./actions/index').placeActions} PlaceActions
 * @typedef {typeof import('./actions/index').seriesActions} SeriesActions
 * @typedef {typeof import('./actions/index').tagActions} TagActions
 * @typedef {typeof import('./actions/index').uiActions} UIActions
 * @typedef {typeof import('./actions/index').hierarchyActions} HierarchyActions
 * @typedef {typeof import('./actions/index').featureFlagsActions} FeatureFlagsActions
 * @typedef {typeof import('./actions/index').errorActions} ErrorActions
 * @typedef {typeof import('./actions/index').projectActions} ProjectActions
 * @typedef {typeof import('./actions/index').clientActions} ClientActions
 * @typedef {typeof import('./actions/index').licenseActions} LicenseActions
 * @typedef {typeof import('./actions/index').knownFilesActions} KnownFilesActions
 * @typedef {typeof import('./actions/index').templatesActions} TemplatesActions
 * @typedef {typeof import('./actions/index').settingsActions} SettingsActions
 * @typedef {typeof import('./actions/index').backupsActions} BackupsActions
 * @typedef {typeof import('./actions/index').applicationStateActions} ApplicationStateActions
 * @typedef {typeof import('./actions/index').imageCacheActions} ImageCacheActions
 * @typedef {typeof import('./actions/index').notificationsActions} NotificationsActions
 * @typedef {typeof import('./actions/index').domEventsActions} DomEventsActions
 * @typedef {typeof import('./actions/index').testingAndDiagnosisActions} TestingAndDiagnosisActions
 * @typedef {typeof import('./actions/index').attributesActions} AttributesActions
 * @typedef {typeof import('./actions/index').undoActions} UndoActions
 * @typedef {typeof import('./actions/index').offlineActions} OfflineActions
 * @typedef {typeof import('./actions/index').accountActions} AccountActions
 * @typedef {typeof import('./actions/index').familyTreesActions} FamilyTreesActions
 * @typedef {typeof import('./actions/index').ideasActions} IdeasActions
 * @typedef {typeof import('./actions/index').audioCacheActions} AudioCacheActions
 */

/**
 * @typedef {String} ImageDataURL
 */

/**
 * @typedef {import('./types/system').FontsUserAppSettings} FontSettings
 */

/**
 * @typedef Coord
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {LastClickDomEvent} Click
 */

/**
 * @typedef PlottrState
 * @property {PlottrFile} user
 * @property {SystemState} [system]
 */

/**
 * @typedef {import('./actions/index').PltrAction} PltrAction
 */

/**
 * @typedef {String} StorageURL
 * @typedef {String} ImageDataURI
 * @typedef {String} FileURL
 * @typedef {String} ExternalURL
 */

/**
 * @typedef {import('./types/system').KnownFilesEntry} KnownFile
 */

const PREVIEW_PANEL_TYPES = ['notes', 'cards', 'characters', 'places']
const BOOKMARK_TYPES = ['notes', 'cards', 'characters', 'places']

const reducers = {
  customAttributes: customAttributesReducer,
  lines: linesReducer,
  beats: beatsReducer,
  books: booksReducer,
  cards: cardsReducer,
  categories: categoriesReducer,
  characters: charactersReducer,
  images: imagesReducer,
  notes: notesReducer,
  places: placesReducer,
  series: seriesReducer,
  tags: tagsReducer,
  file: fileReducer,
  ui: uiReducer,
  hierarchyLevels: hierarchyReducer,
  error: errorReducer,
  permission: permissionReducer,
  client: clientReducer,
  license: licenseReducer,
  knownFiles: knownFilesReducer,
  templates: templatesReducer,
  settings: settingsReducer,
  account: accountReducer,
  backups: backupsReducer,
  applicationState: applicationStateReducer,
  imageCache: imageCacheReducer,
  notifications: notificationsReducer,
  domEvents: domEventsReducer,
  testingAndDiagnosis: testingAndDiagnosisReducer,
  familyTrees: familyTreesReducer,
  ideas: ideasReducer,
  audioCache: audioCacheReducer,
}

const helpers = {
  card: cardHelpers,
  beats: beatHelpers,
  books: bookHelpers,
  lists: listHelpers,
  orientedClassName: orientedClassNameHelpers,
  lines: lineHelpers,
  hierarchyLevels: hierarchyHelpers,
  colors: colorHelpers,
  time: timeHelpers,
  date: dateHelpers,
  file: fileHelpers,
  template: templatesHelpers,
  characters: characterHelpers,
  ui: uiHelpers,
  text: textHelpers,
  json: jsonHelpers,
  promise: promiseHelpers,
  safeParseInt,
  hash,
  string: stringHelpers,
}

const slate = {
  schema: {
    validRCESchema,
    validHTMLSchema: validHTMLRCESchema,
  },
  rtf: { serialize: serializeToRTF },
  plain: { serialize: serializeToPlain, serializeNoFormatting },
  html: {
    deserialise: convertHTMLString,
    deserialiseHTMLNodeList: convertHTMLNodeList,
    serialise: slateToHTML,
  },
}

const rtfSerialisersAndDeserialisers = {
  toHTML: rtfToHTML,
}

const middlewares = {
  externalSync,
  externalSyncWithoutHistory,
}

const specialCaseFixes = {
  applyAllFixes,
  addHierarchiesIfMissing,
  addUITimelineOrHierarchiesStateIfMissing,
}

const schema = {
  plottr: {
    validPlottrFileOrNull,
  },
  knownFiles: {
    onlyValidKnownFiles,
    validKnownFileOrNull,
  },
  trial: {
    validTrialOrNull,
  },
  customTemplates: {
    keepOnlyValidCustomTemplates,
    fixInvalidCustomTemplate,
  },
  templates: {
    keepOnlyValidTemplates,
    validBuiltInTemplateOrNull,
  },
  export: {
    validExportSettingsOrDefault,
  },
  settings: {
    validAppSettings,
  },
  backups: {
    keepOnlyValidBackups,
  },
  images: {
    keepOnlyValidImageCacheEntries,
  },
  license: {
    validLicenseOrNull,
  },
  offlineFiles: {
    keepOnlyValidOfflineFiles,
    keepOnlyValidCreatedOfflineFiles,
    validCreatedOfflineFilesEntryOrNull,
  },
}

export {
  actions,
  ActionTypes,
  LoadActions,
  helpers,
  colors,
  errorCodes,
  migrateIfNeeded,
  Migrator,
  rootReducer,
  mainReducer,
  SYSTEM_REDUCER_KEYS,
  SYSTEM_REDUCER_ACTION_TYPES,
  removeSystemKeys,
  reducers,
  selectors,
  initialState,
  lineColors,
  emptyFile,
  newIds,
  template,
  tree,
  borderStyle,
  defaultSettings,
  slate,
  middlewares,
  ARRAY_KEYS,
  checkFileIntegrity,
  specialCaseFixes,
  editStates,
  rtf,
  rtfSerialisersAndDeserialisers,
  PREVIEW_PANEL_TYPES,
  roughlyMatchesPlottrFileSchema,
  plottrSchema,
  schema,
  versionControl,
  pocketTypes,
  BOOKMARK_TYPES,
  applyAllFixes,
}
